/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\TransactionHistory.jsx
 * ВЕРСИЯ: 2.1.0 – SENT / RECEIVED + SELF AS RECEIVED
 * ДАТА: 18.11.2025
 */

import React, { useState, useEffect } from 'react';
import './TransactionHistory.css';

const API_URL = 'https://qr-money.onrender.com/api';

const TransactionHistory = ({ userId: propUserId }) => {
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [user, setUser] = useState(null);
  const [filter, setFilter] = useState('all'); // all | sent | received
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const actualUserId = propUserId || localStorage.getItem('userId');
  const token = localStorage.getItem('authToken');

  // ===== HELPERS =====================================================

  const asInt = (value) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? null : n;
  };

  const isSent = (tx, uid) =>
    tx && asInt(tx.from_user_id) === uid && asInt(tx.to_user_id) !== uid;

  const isReceived = (tx, uid) =>
    tx && asInt(tx.to_user_id) === uid && asInt(tx.from_user_id) !== uid;

  const isSelfTransfer = (tx, uid) =>
    tx && asInt(tx.from_user_id) === uid && asInt(tx.to_user_id) === uid;

  const formatAmount = (value) => {
    const n = parseFloat(value || 0);
    if (Number.isNaN(n)) return '0.00';
    return n.toFixed(2);
  };

  // ===== ЗАГРУЗКА ДАННЫХ =============================================

  useEffect(() => {
    if (!actualUserId || !token) {
      setError('Пользователь не авторизован');
      setLoading(false);
      return;
    }

    fetchUserProfile();
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualUserId]);

  const fetchUserProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/profile/${actualUserId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) return;

      const data = await res.json();
      if (data.success && data.data) {
        setUser({
          id: data.data.id,
          email: data.data.email,
          phone: data.data.phone,
        });
      }
    } catch (e) {
      console.warn('Profile load error:', e);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('📥 Loading transactions for userId=', actualUserId);

      const res = await fetch(
        `${API_URL}/transactions/user/${actualUserId}?limit=200&offset=0`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(
          errData.message || `Ошибка загрузки транзакций (${res.status})`
        );
      }

      const json = await res.json();
      console.log('✅ RAW transactions response:', json);

      let items = [];

      if (Array.isArray(json.data)) {
        items = json.data;
      } else if (json.data && Array.isArray(json.data.items)) {
        items = json.data.items;
      }

      items = items.filter(
        (tx) => tx && typeof tx === 'object' && typeof tx.id !== 'undefined'
      );

      console.log('✅ Итоговый список транзакций:', items.length);

      setAllTransactions(items);
    } catch (err) {
      console.error('❌ Ошибка загрузки транзакций:', err);
      setError(err.message || 'Ошибка загрузки транзакций');
    } finally {
      setLoading(false);
    }
  };

  // ===== ПРИМЕНЕНИЕ ФИЛЬТРА =========================================

  useEffect(() => {
    const uid = asInt(actualUserId);
    if (!uid) {
      setFilteredTransactions([]);
      return;
    }

    let list = allTransactions;

    if (filter === 'sent') {
      list = allTransactions.filter((tx) => isSent(tx, uid));
    } else if (filter === 'received') {
      // В полученных учитываем и обычные входящие, и системные операции
      list = allTransactions.filter(
        (tx) => isReceived(tx, uid) || isSelfTransfer(tx, uid)
      );
    }

    setFilteredTransactions(list);
  }, [filter, allTransactions, actualUserId]);

  // ===== СТАТЫ ДЛЯ ТАБОВ/ИНФО =======================================

  const uidInt = asInt(actualUserId) || 0;

  const totalCount = allTransactions.length;
  const sentCount = allTransactions.filter((tx) =>
    isSent(tx, uidInt)
  ).length;
  const receivedCount = allTransactions.filter(
    (tx) => isReceived(tx, uidInt) || isSelfTransfer(tx, uidInt)
  ).length;

  const completedCount = allTransactions.filter(
    (tx) => tx.status === 'completed'
  ).length;

  const totalSentAmount = allTransactions
    .filter((tx) => isSent(tx, uidInt))
    .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);

  const totalReceivedAmount = allTransactions
    .filter((tx) => isReceived(tx, uidInt) || isSelfTransfer(tx, uidInt))
    .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);

  const mainCurrency = allTransactions[0]?.currency || 'DAR';

  // ===== РЕНДЕР СОСТОЯНИЙ ===========================================

  if (loading) {
    return (
      <div className="transactions-loading">⏳ Загрузка транзакций...</div>
    );
  }

  if (error) {
    return (
      <div className="transactions-error">
        <p>❌ {error}</p>
        <button onClick={fetchTransactions} className="retry-btn">
          🔄 Повторить
        </button>
      </div>
    );
  }

  // ===== ОСНОВНОЙ РЕНДЕР ============================================

  return (
    <div className="transactions-container">
      <div className="transactions-header">
        <h1>📋 История транзакций</h1>
        {user && (
          <p className="transactions-subtitle">
            Пользователь ID {user.id} • {user.email}
          </p>
        )}
      </div>

      {/* ФИЛЬТРЫ */}
      <div className="filter-group">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          📚 Все ({totalCount})
        </button>
        <button
          className={`filter-btn ${filter === 'sent' ? 'active' : ''}`}
          onClick={() => setFilter('sent')}
        >
          📤 Отправленные ({sentCount})
        </button>
        <button
          className={`filter-btn ${filter === 'received' ? 'active' : ''}`}
          onClick={() => setFilter('received')}
        >
          📥 Полученные ({receivedCount})
        </button>
      </div>

      {/* СПИСОК ТРАНЗАКЦИЙ */}
      {filteredTransactions.length === 0 ? (
        <div className="no-transactions">
          <p>❌ Транзакции не найдены</p>
        </div>
      ) : (
        <div className="transactions-list">
          {filteredTransactions.map((tx) => {
            const uid = uidInt;
            const sent = isSent(tx, uid);
            const received = isReceived(tx, uid);
            const self = isSelfTransfer(tx, uid);

            let title = 'Транзакция';
            let icon = '🔁';
            let sign = '';

            if (sent) {
              title = 'Отправлено';
              icon = '📤';
              sign = '-';
            } else if (received) {
              title = 'Получено';
              icon = '📥';
              sign = '+';
            } else if (self) {
              title = 'Системная операция';
              icon = '🔁';
              sign = '';
            }

            return (
              <div key={tx.id} className="transaction-item">
                <div className="transaction-icon">{icon}</div>
                <div className="transaction-details">
                  <div className="transaction-top">
                    <p className="transaction-user">{title}</p>
                    <span className="transaction-amount">
                      {sign}
                      {formatAmount(tx.amount)} {tx.currency || mainCurrency}
                    </span>
                  </div>
                  <div className="transaction-bottom">
                    <span className="transaction-date">
                      {tx.created_at
                        ? new Date(tx.created_at).toLocaleString('ru-RU')
                        : '—'}
                    </span>
                    <span
                      className={`transaction-status status-${tx.status || 'unknown'
                        }`}
                    >
                      {tx.status === 'completed' && '✅ Завершено'}
                      {tx.status === 'pending' && '⏳ В ожидании'}
                      {tx.status === 'failed' && '❌ Ошибка'}
                      {!tx.status && 'Статус неизвестен'}
                    </span>
                    <div className="transaction-id">
                      <small>ID: {tx.id}</small>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ИТОГОВЫЙ БЛОК */}
      {allTransactions.length > 0 && (
        <div className="transactions-summary">
          <h2>📊 Сводка</h2>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Всего операций</span>
              <span className="summary-value">{totalCount}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Завершено</span>
              <span className="summary-value">{completedCount}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Отправлено всего</span>
              <span className="summary-value">
                {formatAmount(totalSentAmount)} {mainCurrency}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Получено всего</span>
              <span className="summary-value">
                {formatAmount(totalReceivedAmount)} {mainCurrency}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
